import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  BaseQueryApi
} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { API_URL } from 'constants/environment';

import type { RootState } from 'reduxLocal/store';

import type { AuthResponse } from './authenticationApi';

import { setCredentials, expired } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_URL}/api`,
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as RootState).auth;

    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  }
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

  // If access token is missing from state when logged in - fetch it
  const state = api.getState();
  if (
    isLoggedIn &&
    (!state || !state['auth'] || !state['auth']['accessToken'])
  ) {
    await refreshAccessToken(api, extraOptions);
  }

  let result = await baseQuery(args, api, extraOptions);

  // If query failed with 401 - refresh access token and retry
  if (result.error && result.error.status === 401) {
    await refreshAccessToken(api, extraOptions);
    result = await baseQuery(args, api, extraOptions);
  }
  return result;
};

const refreshAccessToken = async (api: BaseQueryApi, extraOptions: object) => {
  try {
    const { data } = await baseQuery(
      {
        url: '/v1/authentication/token/refresh',
        method: 'POST',
        credentials: 'include'
      },
      api,
      extraOptions
    );

    if (data) {
      api.dispatch(setCredentials(data as AuthResponse));
    } else {
      api.dispatch(expired());
    }
  } catch (e) {
    console.error(e);
  }
};
